<template>
    <div class="autocomplete-wrapper">
        <b-input-group class="autocomplete-input-group" :class="{'is-focused': isListVisible}">
            <b-input-group-prepend v-if="loading" is-text>
                <b-spinner small></b-spinner>
            </b-input-group-prepend>

            <b-form-input
                ref="input"
                v-model="searchQuery"
                :placeholder="placeholder"
                :readonly="loading || readonly"
                :disabled="loading || disabled"
                autocomplete="off"
                :state="state"
                @focus="showList"
                @click="showList"
                @keydown.esc="hideList"
                @keydown.down.prevent="onArrowDown"
                @keydown.up.prevent="onArrowUp"
                @keydown.enter.prevent="onEnter"
            ></b-form-input>

            <b-input-group-append>
                <b-button v-if="clearable && (searchQuery && !loading)" variant="outline-secondary" class="clear-btn" @click="clearInput">
                    <fa :icon="['fas', 'times-circle']" />
                </b-button>
                <b-button :disabled="loading || disabled" variant="outline-secondary" class="dropdown-btn" @click="toggleList">
                    <fa :icon="['fas', 'chevron-down']" />
                </b-button>
            </b-input-group-append>
        </b-input-group>

        <!-- 포털 렌더링: 드롭다운을 전역 타겟으로 출력 -->
        <portal to="global-dropdown" v-if="isListVisible && filteredOptions.length > 0">
            <div class="autocomplete-portal" :style="portalStyle" ref="portalRoot">
                <b-list-group class="autocomplete-results" :style="{ maxHeight: listMaxHeight }">
                    <template v-for="(option, index) in filteredOptions">
                        <b-list-group-item
                            v-if="option.isGroupHeader"
                            :key="`group-${option.label}`"
                            class="font-weight-bold bg-light"
                            button
                            @click="toggleGroup(option.label)"
                        >
                            <div class="d-flex justify-content-between align-items-center">
                                {{ option.label }}
                                <fa :icon="['fas', isGroupOpen(option.label) ? 'chevron-up' : 'chevron-down']" />
                            </div>
                        </b-list-group-item>

                        <b-list-group-item
                            v-else-if="isGroupOpen(option.group)"
                            :id="`autocomplete-item-${getNavigatableIndex(option)}`"
                            :key="`option-${getOptionValue(option)}-${index}`"
                            button
                            :active="focusedIndex === getNavigatableIndex(option)"
                            :variant="isOptionSelected(option) ? 'success' : null"
                            class="d-flex justify-content-between align-items-center"
                            @mousedown.prevent="selectOption(option)"
                        >
                            <span class="pl-3">{{ getOptionText(option) }}</span>
                            <fa v-if="isOptionSelected(option)" :icon="['fas', 'check']" />
                        </b-list-group-item>
                    </template>
                </b-list-group>
            </div>
        </portal>
    </div>
</template>

<script>
export default {
    name: 'AutoCompleteSelect',
    inheritAttrs: false,
    model: {
        prop: 'selectedId',
        event: 'input'
    },
    props: {
        selectedId: { type: [Number, String, null], default: null },
        options: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },
        placeholder: { type: String, default: null },
        optionTextKey: { type: String, default: 'text' },
        optionValueKey: { type: String, default: 'value'},
        startExpanded: { type: Boolean, default: false },
        initiallyExpandedGroups: { type: Array, default: () => [] },
        listMaxHeight: { type: String, default: '520px' },
        state: { type: [Boolean, null], default: null },
    },
    data() {
        return {
            searchQuery: '',
            isListVisible: false,
            focusedIndex: -1,
            openGroups: [...this.initiallyExpandedGroups],
            isProgrammaticChange: false,
            // 포털 위치
            portalRect: { top: 0, left: 0, width: 0 },
        };
    },
    computed: {
        filteredOptions() {
            const query = this.searchQuery ? this.searchQuery.toLowerCase() : '';
            const results = [];
            if (!this.options) return [];
            this.options.forEach(option => {
                if (option.group && option.items) {
                    const filteredItems = query ? option.items.filter(item =>
                        this.getOptionText(item).toLowerCase().includes(query)
                    ) : [...option.items];

                    if (filteredItems.length > 0) {
                        results.push({ isGroupHeader: true, label: option.group });
                        filteredItems.forEach(item => {
                            results.push({ ...item, group: option.group });
                        });
                    }
                } else if (!query || this.getOptionText(option).toLowerCase().includes(query)) {
                    results.push(option);
                }
            });
            return results;
        },
        navigatableOptions() {
            return this.filteredOptions.filter(opt => !opt.isGroupHeader && this.isGroupOpen(opt.group));
        },
        flattenOptions() {
            return this.options.reduce((acc, opt) => {
                if (opt.group && opt.items) {
                    opt.items.forEach(item => { acc[item.id] = item; });
                } else { acc[opt.id] = opt; }
                return acc;
            }, {});
        },
        selectedItem(){
            if (this.selectedId != null) return this.flattenOptions[this.selectedId];
            return null;
        },
        portalStyle(){
            return {
                position: 'fixed',
                top: this.portalRect.top + 'px',
                left: this.portalRect.left + 'px',
                width: this.portalRect.width + 'px',
                zIndex: 3000,
            };
        }
    },
    watch: {
        selectedItem: {
            immediate: true,
            handler(newValue) {
                this.isProgrammaticChange = true;
                this.searchQuery = this.getOptionText(newValue);
            }
        },
        searchQuery(newValue, oldValue){
            if (this.isProgrammaticChange) { this.isProgrammaticChange = false; return; }
            if (newValue !== oldValue) {
                this.focusedIndex = -1;
                this.showList();
            }
        },
        isListVisible(val){
            if (val) {
                this.$nextTick(() => {
                    this.updatePortalPosition();
                    window.addEventListener('resize', this.updatePortalPosition, { passive: true });
                    window.addEventListener('scroll', this.onAnyScroll, true);
                    document.addEventListener('click', this.handleClickOutside, true);
                });
            } else {
                window.removeEventListener('resize', this.updatePortalPosition, { passive: true });
                window.removeEventListener('scroll', this.onAnyScroll, true);
                document.removeEventListener('click', this.handleClickOutside, true);
            }
        }
    },
    created() {
        if (this.startExpanded) this.expandAllGroups();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updatePortalPosition, { passive: true });
        window.removeEventListener('scroll', this.onAnyScroll, true);
        document.removeEventListener('click', this.handleClickOutside, true);
    },
    methods: {
        expandAllGroups() {
            const allGroups = this.options.filter(opt => opt.group).map(opt => opt.group);
            this.openGroups = [...new Set([...allGroups, ...this.initiallyExpandedGroups])];
        },
        isGroupOpen(groupName) { if (!groupName) return true; return this.openGroups.includes(groupName); },
        toggleGroup(groupName) {
            this.focusedIndex = -1;
            const index = this.openGroups.indexOf(groupName);
            if (index > -1) this.openGroups.splice(index, 1); else this.openGroups.push(groupName);
        },
        onArrowDown() {
            if (!this.isListVisible || this.navigatableOptions.length === 0) return;
            this.focusedIndex = (this.focusedIndex + 1) % this.navigatableOptions.length;
            this.scrollToFocusedItem();
        },
        onArrowUp() {
            if (!this.isListVisible || this.navigatableOptions.length === 0) return;
            this.focusedIndex = (this.focusedIndex - 1 + this.navigatableOptions.length) % this.navigatableOptions.length;
            this.scrollToFocusedItem();
        },
        onEnter() {
            if (!this.isListVisible) return;
            let selected = null;
            if (this.focusedIndex > -1) selected = this.navigatableOptions[this.focusedIndex];
            else if (this.navigatableOptions.length > 0) selected = this.navigatableOptions[0];
            if (selected) this.selectOption(selected);
        },
        scrollToFocusedItem() {
            this.$nextTick(() => {
                if (this.focusedIndex === -1) return;
                const activeItem = document.getElementById(`autocomplete-item-${this.focusedIndex}`);
                if (activeItem) activeItem.scrollIntoView({ block: 'nearest' });
            });
        },
        scrollToSelectedItem() {
            if (!this.selectedItem) return;
            const idx = this.getNavigatableIndex(this.selectedItem);
            if (idx === -1) return;
            const el = document.getElementById(`autocomplete-item-${idx}`);
            if (el) { el.scrollIntoView({ block: 'center' }); this.focusedIndex = idx; }
        },
        getNavigatableIndex(option) {
            return this.navigatableOptions.findIndex(item => this.getOptionValue(item) === this.getOptionValue(option));
        },
        getOptionValue(option) {
            if (option == null) return null;
            if (typeof option === 'object') return option[this.optionValueKey] || option[this.optionTextKey] || null;
            return option;
        },
        isOptionSelected(option) {
            if (!this.selectedItem || !option) return false;
            return this.getOptionValue(this.selectedItem) === this.getOptionValue(option);
        },
        getOptionText(option) {
            if (option == null) return '';
            if (typeof option === 'object') return option[this.optionTextKey] || '';
            return String(option);
        },
        selectOption(option) {
            this.isListVisible = false;
            this.isProgrammaticChange = true;
            this.searchQuery = this.getOptionText(option);
            this.$emit('input', option.id);
            this.$emit('click', option);
        },
        clearInput() {
            this.searchQuery = '';
            this.$emit('input', null);
            this.$nextTick(() => { this.$refs.input.focus(); this.showList(); });
        },
        showList() {
            if (this.disabled || this.readonly) return;
            if (!this.isListVisible && this.selectedItem) {
                this.isProgrammaticChange = true;
                this.searchQuery = '';
            }
            this.isListVisible = true;
            this.$nextTick(() => { this.updatePortalPosition(); this.scrollToSelectedItem(); });
        },
        hideList() {
            if (!this.isListVisible) return;
            if (this.selectedItem === null || this.searchQuery !== this.getOptionText(this.selectedItem)) {
                this.isProgrammaticChange = true;
                this.searchQuery = this.getOptionText(this.selectedItem);
            }
            this.isListVisible = false;
            this.focusedIndex = -1;
        },
        toggleList() { if (this.isListVisible) this.hideList(); else this.showList(); },
        updatePortalPosition() {
            const anchor = this.$el.querySelector('.autocomplete-input-group');
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();
            this.portalRect = { top: rect.bottom, left: rect.left, width: rect.width };
        },
        onAnyScroll() { if (this.isListVisible) this.updatePortalPosition(); },
        handleClickOutside(event) {
            const clickedInRoot = this.$el && this.$el.contains(event.target);
            const portalRoot = this.$refs.portalRoot;
            const clickedInPortal = portalRoot && portalRoot.contains(event.target);
            if (!clickedInRoot && !clickedInPortal) this.hideList();
        },
    }
};
</script>

<style lang="scss" scoped>
.autocomplete-wrapper {
    position: relative;
    width: 100%;
    display: block;
}
.list-group-item.active {
    background-color: #e9ecef;
    border-color: #dee2e6;
    color: #212529;
}
.autocomplete-portal { position: fixed; z-index: 3000; }
.autocomplete-results {
    position: static; // 포털 컨테이너가 위치를 담당
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-top: none;
    border-bottom-left-radius: .25rem;
    border-bottom-right-radius: .25rem;
    background: #fff;
}
.autocomplete-input-group {
    .input-group-append .btn {
        background-color: white;
        border-color: #ced4da;
        &:hover { background-color: #e9ecef; }
        &:disabled{ background-color: #e9ecef; }
    }
    .input-group-prepend + .form-control,
    .form-control + .input-group-append,
    .input-group-append .btn + .btn { margin-left: -1px; }
    &.is-focused {
        $focus-color: #80bdff;
        .form-control,
        .input-group-prepend,
        .input-group-append .btn {
            border-color: $focus-color;
            box-shadow: 0 0 0 0.2rem rgba($focus-color, .25);
        }
        .form-control,
        .input-group-prepend { z-index: 1; }
        .input-group-append .btn { z-index: 2; }
    }
    // 실패/성공 상태에 따른 append 버튼 색상
    .form-control.is-invalid + .input-group-append .btn { border-color: #dc3545 !important; }
    .form-control.is-invalid + .input-group-append .btn:hover { border-color: #dc3545 !important; }
    .form-control.is-valid + .input-group-append .btn { border-color: #28a745 !important; }
    .form-control.is-valid + .input-group-append .btn:hover { border-color: #28a745 !important; }
}
</style>
